import { formLabels } from '../types';
import type { DashboardRow } from '../dashboardTypes';
import { formatDay, formatMoney } from '../format';
import { StatusBadge } from './StatusBadge';

/** Payment Queue (SRS 14): the server's priority-ordered projection of the payments that need someone. Never a second source of truth: it only renders `queue`. */
export function PaymentQueue({ rows, loading, showCompany, companyName, onOpen }: {
  rows: DashboardRow[];
  loading: boolean;
  showCompany: boolean;
  companyName: (companyId: string) => string | null;
  onOpen: (row: DashboardRow, opener: HTMLElement) => void;
}) {
  return (
    <section className="tpd-side-card" aria-labelledby="tpd-queue-title" data-testid="payment-queue">
      <div className="tpd-side-card__head">
        <h3 id="tpd-queue-title" className="tpd-side-card__title">Payment Queue</h3>
        {!loading && <span className="tpd-side-card__count" aria-label={`${rows.length} in the queue`}>{rows.length}</span>}
      </div>
      {loading && <p className="tpd-muted">Loading the queue…</p>}
      {!loading && rows.length === 0 && <p className="tpd-muted" data-testid="queue-empty">Nothing needs action right now.</p>}
      {!loading && rows.length > 0 && (
        <ul className="tpd-queue" aria-label="Payments that need action">
          {rows.map((row) => {
            const form = formLabels[row.formCode] ?? row.formCode;
            return (
              <li key={row.id}>
                <button type="button" className="tpd-queue__item" onClick={(e) => onOpen(row, e.currentTarget)} aria-label={`Open ${form} payment, ${formatMoney(row.amount)}, due ${formatDay(row.dueDate)}`}>
                  <span className="tpd-queue__top">
                    <span className="tpd-queue__name">{showCompany ? (companyName(row.companyId) ?? 'Company') : form}</span>
                    <StatusBadge status={row.status} />
                  </span>
                  <span className="tpd-queue__sub">{showCompany ? `${form} · ` : ''}{row.agency} · Due {formatDay(row.dueDate)}</span>
                  <span className="tpd-queue__amount">{formatMoney(row.amount)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
